// src/auditoria/components/ds/DsCellLink.tsx
//
// Implementa a classe do canvas `.link` — 82 declarações, instanciada em
// 72 artboards (DESIGN-SYSTEM.md §4.4).
//
// `color:var(--info-main);font-weight:600;font-size:13px;background:none;border:0;
//  padding:0;cursor:pointer;font-family:inherit;text-align:left` ·
// `.link:disabled{color:var(--text-disabled);cursor:default}`, mais
// `max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap`.
//
// Dois acréscimos ao canvas, por acessibilidade, e só estes dois: sublinhado no
// hover e anel de foco de 2px em `primary.main` (o reset do próprio artboard já
// prescreve o anel).
//
// É `styled('button')` com `type="button"` (ou `'a'` quando há `href`).
// **Não** `Button` (padding 6/8, `min-width` 64, ripple, cor primary) e
// **não** `Link` do MUI (14px, primary).
//
// Convenção do canvas: exatamente **uma** coluna por linha é clicável, sempre a
// primeira coluna textual (Nome, Contribuinte, Descrição, Protocolo). As demais
// ficam em `text.primary`. O helper `colunaLink` decora essa coluna com uma linha.

import type { ReactNode, MouseEvent } from 'react';
import type { Theme, CSSObject } from '@mui/material/styles';
import type { GridColDef, GridValidRowModel, GridRenderCellParams } from '@mui/x-data-grid';

import { styled } from '@mui/material/styles';

import { DS_MONO } from './tokens';

// ----------------------------------------------------------------------

export type DsCellLinkProps = {
  children: ReactNode;
  onClick?: (e: MouseEvent) => void;
  href?: string;
  disabled?: boolean;
  mono?: boolean;
  title?: string;
};

type EstiloLink = { mono: boolean };

const estiloLink = (theme: Theme, { mono }: EstiloLink): CSSObject => ({
  display: 'block',
  maxWidth: '100%',
  // A célula da grade virou `display:flex` para centrar o conteúdo na altura da
  // linha; sem `minWidth:0` o item flex não encolhe e o `text-overflow` abaixo
  // nunca dispara.
  minWidth: 0,
  color: theme.vars.palette.info.main,
  fontWeight: 600,
  fontSize: 13,
  lineHeight: 1.5,
  background: 'none',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  fontFamily: mono ? DS_MONO : 'inherit',
  textAlign: 'left',
  textDecoration: 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:hover': { textDecoration: 'underline' },
  '&:focus-visible': {
    outline: `2px solid ${theme.vars.palette.primary.main}`,
    outlineOffset: 2,
  },
  '&:disabled': {
    color: theme.vars.palette.text.disabled,
    cursor: 'default',
    textDecoration: 'none',
  },
});

const naoRepassar = (prop: PropertyKey) => prop !== 'mono';

const BotaoLink = styled('button', { shouldForwardProp: naoRepassar })<EstiloLink>(
  ({ theme, mono }) => estiloLink(theme, { mono })
);

const AncoraLink = styled('a', { shouldForwardProp: naoRepassar })<EstiloLink>(({ theme, mono }) =>
  estiloLink(theme, { mono })
);

export function DsCellLink({
  children,
  onClick,
  href,
  disabled = false,
  mono = false,
  title,
}: DsCellLinkProps) {
  // Âncora desabilitada não existe em HTML: cai para o botão desabilitado.
  if (href && !disabled) {
    return (
      <AncoraLink href={href} title={title} mono={mono} onClick={onClick}>
        {children}
      </AncoraLink>
    );
  }

  return (
    <BotaoLink type="button" title={title} mono={mono} disabled={disabled} onClick={onClick}>
      {children}
    </BotaoLink>
  );
}

// ----------------------------------------------------------------------

/**
 * Decora uma coluna existente injetando o `renderCell`. Uma linha por coluna.
 *
 * ```tsx
 * const cols = [
 *   { field: 'id', headerName: 'ID', width: 64 },
 *   colunaLink({ field: 'nome', headerName: 'Nome', flex: 1 }, (row) => abrir(row.id)),
 *   { field: 'sigla', headerName: 'Sigla', width: 140 },
 * ];
 * ```
 */
export function colunaLink<T extends GridValidRowModel>(
  col: GridColDef<T>,
  aoAbrir: (row: T) => void
): GridColDef<T> {
  return {
    ...col,
    renderCell: (params: GridRenderCellParams<T>) => {
      const bruto = params.formattedValue ?? params.value;

      if (bruto === null || bruto === undefined || bruto === '') {
        return null;
      }

      const texto = String(bruto);

      return (
        <DsCellLink
          title={texto}
          onClick={(evento) => {
            // A grade também escuta o clique na linha; o link é o dono do gesto.
            evento.stopPropagation();
            aoAbrir(params.row);
          }}
        >
          {texto}
        </DsCellLink>
      );
    },
  };
}
